import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------- Global Fetch Guard ----------
// Log any 4xx / 5xx response to a lightweight telemetry bin so operators can
// spot systemic failures before users report them. Fire-and-forget — never
// blocks the caller, never chains onto their catch. Dedupe by URL+status
// within a short window so a flaky poll can't spam the endpoint.
const RECENT = new Map(); // key: `${method}:${url}:${status}` → lastTs
const RECENT_WINDOW_MS = 30 * 1000;

const shouldLog = (key) => {
  const now = Date.now();
  const last = RECENT.get(key) || 0;
  if (now - last < RECENT_WINDOW_MS) return false;
  RECENT.set(key, now);
  // Very light memory cap
  if (RECENT.size > 100) {
    const cutoff = now - RECENT_WINDOW_MS;
    for (const [k, ts] of RECENT.entries()) if (ts < cutoff) RECENT.delete(k);
  }
  return true;
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      // Only report real HTTP errors (skip pure network cancellations, aborts, etc.)
      if (status && status >= 400) {
        const cfg = err.config || {};
        const url = (cfg.baseURL ? "" : "") + (cfg.url || "unknown");
        // Never re-emit telemetry for the telemetry endpoint itself — that'd loop.
        const isTelemetryEndpoint = url.includes("/telemetry/frontend-error");
        const key = `${(cfg.method || "get").toUpperCase()}:${url}:${status}`;
        if (!isTelemetryEndpoint && shouldLog(key)) {
          // Fire-and-forget — bypass the shared `api` instance to avoid interceptor
          // recursion, and swallow every failure silently.
          const payload = {
            status,
            method: (cfg.method || "get").toUpperCase(),
            url,
            message: (err.message || "").slice(0, 400),
            page: (typeof window !== "undefined" ? window.location.pathname : "") || "",
            ua: (typeof navigator !== "undefined" ? navigator.userAgent : "") || "",
          };
          if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            try {
              const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
              navigator.sendBeacon(`${API}/telemetry/frontend-error`, blob);
            } catch (_) { /* silent */ }
          } else {
            // Fallback for environments without sendBeacon
            axios
              .post(`${API}/telemetry/frontend-error`, payload, { withCredentials: false, timeout: 3000 })
              .catch(() => {});
          }
        }
      }
    } catch (_) { /* never let telemetry break the app */ }
    return Promise.reject(err);
  }
);

// ---------- Runtime-error Overlay Suppressor ----------
// The actual listener is installed in /public/index.html (in <head>, before
// the app bundle runs) so it beats CRA's react-error-overlay to the
// unhandledrejection event. See the inline <script> comment there.

